/**
 * Implementation of IFloodgateStorageProvider for Web SDK
 */

import { IFloodgateStorageProvider } from "@ms-ofb/officefloodgatecore/dist/src/Api/Api";
import * as Window from "./../Window/Window";
import * as Logging from "./../Logging/Logging";
import FileType = IFloodgateStorageProvider.FileType;

interface IFileToKeyMap {
	[fileType: string]: string;
}

let fileTypeToKeyMap: IFileToKeyMap = {};
fileTypeToKeyMap[FileType.CampaignDefinitions.toString()] = "obf-CampaignDefinitions";
fileTypeToKeyMap[FileType.CampaignStates.toString()] = "obf-CampaignStates";
fileTypeToKeyMap[FileType.FloodgateSettings.toString()] = "obf-FloodgateSettings";
fileTypeToKeyMap[FileType.GovernedChannelStates.toString()] = "obf-GovernedChannelStates";
fileTypeToKeyMap[FileType.SurveyActivationStats.toString()] = "obf-SurveyActivationStats";
fileTypeToKeyMap[FileType.SurveyEventActivityStats.toString()] = "obf-SurveyEventActivityStats";

export default class FloodgateStorageProvider implements IFloodgateStorageProvider {
	/**
	 * Code to detect localStorage presence.
	 * Copied from MDN: https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
	 */
	public static isStorageAvailable(): boolean {
		try {
			let x = "__storage_test__";
			Window.get().localStorage.setItem(x, x);
			Window.get().localStorage.removeItem(x);
			return true;
		} catch (e) {
			return e instanceof DOMException && (
				// everything except Firefox
				e.code === 22 ||
				// Firefox
				e.code === 1014 ||
				// test name field too, because code might not be present
				// everything except Firefox
				e.name === "QuotaExceededError" ||
				// Firefox
				e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
				// acknowledge QuotaExceededError only if there's something already stored
				Window.get().localStorage.length !== 0;
		}
	}

	/**
	 * Lock the file to prevent access from another process.
	 */
	public fileLock(fileType: FileType): void {
		// JS runs on a single thread in the browser.
		return;
	}

	/**
	 * Unlock the file.
	 */
	public fileUnlock(fileType: FileType): void {
		// JS runs on a single thread in the browser.
		return;
	}

	/**
	 * Read the file
	 */
	public read(fileType: FileType): string {
		if (!(fileType in FileType) || !fileTypeToKeyMap.hasOwnProperty(fileType.toString())) {
			return "";
		}

		let storageKey = fileTypeToKeyMap[fileType];
		let result: string;
		try {
			result = Window.get().localStorage.getItem(storageKey);
		} catch (e) {
			Logging.getLogger().logEvent(Logging.EventIds.Survey.Floodgate.FileRead.Failed.VALUE,
				{
					ErrorMessage: e.toString(),
					FileName: storageKey,
				});
			return "";
		}

		return result;
	}

	/**
	 * Write to the file
	 */
	public write(fileType: FileType, content: string): void {
		if (!(fileType in FileType) || !fileTypeToKeyMap.hasOwnProperty(fileType.toString())) {
			return;
		}

		let storageKey = fileTypeToKeyMap[fileType];
		try {
			Window.get().localStorage.setItem(storageKey, content);
		} catch (e) {
			Logging.getLogger().logEvent(Logging.EventIds.Survey.Floodgate.FileWrite.Failed.VALUE,
				{
					ErrorMessage: e.toString(),
					FileName: storageKey,
				});
			return;
		}
	}
}
